import AppProviders from "../providers/AppProviders";
import { createBrowserRouter } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Page } from "@beyondrealityapp/core/shared/constants";
import LoadingSpinner from "../components/layout/LoadingSpinner";
import ProtectedRoute from "./ProtectedRoute";
import RedirectSignInUpRoute from "./RedirectSignInRoute";
import SubscriptionGuard from "./SubscriptionGuard";
import NavBarController from "../components/nav_bar/NavBarController";

// Lazy load pages for better performance
const Home = lazy(() => import("../pages/Home"));
const AccountabilityBuddies = lazy(
  () => import("../pages/AccountabilityBuddies")
);
const AdminPanel = lazy(() => import("../pages/AdminPanel"));
const Buddy = lazy(() => import("../pages/Buddy"));
const Checkin = lazy(() => import("../pages/Checkin"));
const Connect = lazy(() => import("../pages/Connect"));
const CoreValues = lazy(() => import("../pages/CoreValues"));
const GoalDetails = lazy(() => import("../pages/GoalDetails"));
const Goals = lazy(() => import("../pages/Goals"));
const Journal = lazy(() => import("../pages/Journal"));
const JournalEntry = lazy(() => import("../pages/JournalEntry"));
const JournalTemplateDetails = lazy(() => import("../pages/JournalTemplateDetails"));
const Notifications = lazy(() => import("../pages/Notifications"));
const Onboarding = lazy(() => import("../pages/Onboarding"));
const PersonalityTests = lazy(() => import("../pages/PersonalityTests"));
const PersonalityTestAnswer = lazy(() => import("../pages/PersonalityTestAnswer"));
const Reviews = lazy(() => import("../pages/Reviews"));
const ReviewDetails = lazy(() => import("../pages/ReviewDetails"));
const Rhythm = lazy(() => import("../pages/Rhythm"));
const SignIn = lazy(() => import("../pages/SignIn"));
const SignUp = lazy(() => import("../pages/SignUp"));
const Subscription = lazy(() => import("../pages/Subscription"));
const SubscriptionBlocked = lazy(
  () => import("../pages/SubscriptionBlocked")
);
const Themes = lazy(() => import("../pages/Themes"));
const ThemeDetails = lazy(() => import("../pages/ThemeDetails"));
const UserProfile = lazy(() => import("../pages/UserProfile"));
const VirtualBuddyDetail = lazy(() => import("../pages/VirtualBuddyDetail"));

// Wrap lazy loaded components with Suspense
const lazyLoad = (Component: React.ComponentType<any>) => (
  <Suspense fallback={<LoadingSpinner />}>
    <Component />
  </Suspense>
);

// Create the router configuration
const router = createBrowserRouter([
  {
    element: <AppProviders />,
    children: [
      {
        element: <ProtectedRoute allowedRoles={["admin", "user"]} />,
        children: [
          {
            path: Page.ONBOARDING,
            element: lazyLoad(Onboarding),
          },
          {
            path: Page.SUBSCRIPTION_BLOCKED,
            element: lazyLoad(SubscriptionBlocked),
          },
          {
            element: <SubscriptionGuard />,
            children: [
              {
                // Consent for apps connecting through the MCP server; no nav bar
                path: Page.CONNECT,
                element: lazyLoad(Connect),
              },
              {
                element: <NavBarController />,
                children: [
                  {
                    path: "/",
                    element: lazyLoad(Home),
                  },
                  {
                    path: Page.CORE_VALUES,
                    element: lazyLoad(CoreValues),
                  },
                  {
                    path: Page.THEMES,
                    element: lazyLoad(Themes),
                  },
                  {
                    path: Page.THEME_DETAILS,
                    element: lazyLoad(ThemeDetails),
                  },
                  {
                    path: Page.GOALS,
                    element: lazyLoad(Goals),
                  },
                  {
                    path: Page.GOAL_DETAILS,
                    element: lazyLoad(GoalDetails),
                  },
                  {
                    path: Page.JOURNAL,
                    element: lazyLoad(Journal),
                  },
                  {
                    path: Page.JOURNAL_ENTRY,
                    element: lazyLoad(JournalEntry),
                  },
                  {
                    path: Page.JOURNAL_TEMPLATE_DETAILS,
                    element: lazyLoad(JournalTemplateDetails),
                  },
                  {
                    path: Page.NOTIFICATIONS,
                    element: lazyLoad(Notifications),
                  },
                  {
                    path: Page.RHYTHM,
                    element: lazyLoad(Rhythm),
                  },
                  {
                    path: Page.CHECKIN,
                    element: lazyLoad(Checkin),
                  },
                  {
                    path: Page.PERSONALITY_TEST,
                    element: lazyLoad(PersonalityTests),
                  },
                  {
                    path: `${Page.PERSONALITY_TEST}/:personalityTestId`,
                    element: lazyLoad(PersonalityTestAnswer),
                  },
                  {
                    path: Page.REVIEWS,
                    element: lazyLoad(Reviews),
                  },
                  {
                    path: Page.REVIEW_DETAILS,
                    element: lazyLoad(ReviewDetails),
                  },
                  {
                    path: Page.ACCOUNTABILITY_BUDDIES,
                    element: lazyLoad(AccountabilityBuddies),
                  },
                  {
                    path: Page.BUDDY_DETAILS,
                    element: lazyLoad(Buddy),
                  },
                  {
                    path: Page.VIRTUAL_BUDDY_DETAILS,
                    element: lazyLoad(VirtualBuddyDetail),
                  },
                  {
                    path: Page.SUBSCRIPTION,
                    element: lazyLoad(Subscription),
                  },
                  {
                    path: Page.USER_PROFILE,
                    element: lazyLoad(UserProfile),
                  },
                  {
                    path: Page.HOME,
                    element: lazyLoad(Home),
                  },
                  {
                    element: <ProtectedRoute allowedRoles={["admin"]} />,
                    children: [
                      {
                        path: Page.ADMIN_PANEL,
                        element: lazyLoad(AdminPanel),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <RedirectSignInUpRoute />,
        children: [
          {
            path: Page.SIGN_IN,
            element: lazyLoad(SignIn),
          },
          {
            path: Page.SIGN_UP,
            element: lazyLoad(SignUp),
          },
        ],
      },
    ],
  },
]);

export default router;
